import { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, SiteCtx } from "@/site/common";
import { MemberCtx, clearToken, hasToken, memberApi } from "@/site/member";
import { Header } from "@/site/Header";
import { Footer } from "@/site/Footer";
import Home from "@/site/Home";
import ListingsPage from "@/site/ListingsPage";
import Detail from "@/site/Detail";
import SellForm from "@/site/SellForm";
import Login from "@/site/auth/Login";
import Register from "@/site/auth/Register";
import { GoogleCallback } from "@/site/auth/GoogleCallback";
import { ForgotPassword, ResetPassword } from "@/site/auth/Recovery";
import Account, { RequireMember } from "@/site/account/Account";
import PostListing from "@/site/post/PostListing";
import PublicProfile from "@/site/PublicProfile";
import VerifyEmail from "@/site/auth/VerifyEmail";
import { About, Contact, NotFound } from "@/site/Pages";
import "@/site/site.css";

function useMemberSession() {
  const go = useNavigate();
  const [member, setMember] = useState(hasToken() ? undefined : null), [favs, setFavs] = useState(() => new Set());
  useEffect(() => {
    if (window.location.hash.includes("session_id=") || !hasToken()) return;
    memberApi.get(`${API}/member/me`).then((r) => setMember(r.data)).catch((e) => { if ([401, 403].includes(e.response?.status)) { clearToken(); if (e.response.status === 403) toast.error(e.response.data?.detail || "Hesabınız askıya alındı"); } setMember(null); });
  }, []);
  const mid = member?.id;
  useEffect(() => { if (!mid) { setFavs(new Set()); return; } memberApi.get(`${API}/member/favorites/codes`).then((r) => setFavs(new Set(r.data))).catch(() => {}); }, [mid]);
  const flip = (code, on) => setFavs((s) => { const n = new Set(s); if (on) n.add(code); else n.delete(code); return n; });
  const toggleFav = async (code) => {
    const on = !favs.has(code); flip(code, on);
    try { if (on) await memberApi.put(`${API}/member/favorites/${code}`); else await memberApi.delete(`${API}/member/favorites/${code}`); return on; }
    catch (e) { flip(code, !on); toast.error(e.response?.data?.detail || "İşlem yapılamadı"); return undefined; }
  };
  const logout = () => { clearToken(); setMember(null); toast.success("Çıkış yapıldı"); go("/"); };
  return { member, setMember, logout, favs, toggleFav };
}

export default function SiteApp() {
  const [info, setInfo] = useState({});
  const { pathname, hash } = useLocation();
  const session = useMemberSession();
  useEffect(() => { axios.get(`${API}/site/info`).then((r) => setInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <SiteCtx.Provider value={info}>
      <MemberCtx.Provider value={session}>
        <div className="site-root" data-testid="site-root">
          <Header />
          <main className="min-h-[60vh]">
            {hash.includes("session_id=") ? <GoogleCallback onDone={session.setMember} /> : (
              <Routes>
                <Route index element={<Home />} />
                <Route path="ilanlar" element={<ListingsPage />} />
                <Route path="ilan/:slug" element={<Detail />} />
                <Route path="hakkimizda" element={<About />} />
                <Route path="iletisim" element={<Contact />} />
                <Route path="evinizi-satalim" element={<SellForm />} />
                <Route path="giris" element={<Login />} />
                <Route path="uye-ol" element={<Register />} />
                <Route path="sifremi-unuttum" element={<ForgotPassword />} />
                <Route path="sifre-yenile" element={<ResetPassword />} />
                <Route path="eposta-dogrula" element={<VerifyEmail />} />
                <Route path="profil/:slug" element={<PublicProfile />} />
                <Route path="hesabim/*" element={<Account />} />
                <Route path="ilan-ver" element={<RequireMember complete><PostListing /></RequireMember>} />
                <Route path="ilan-ver/:id" element={<RequireMember complete><PostListing /></RequireMember>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            )}
          </main>
          <Footer />
        </div>
      </MemberCtx.Provider>
    </SiteCtx.Provider>
  );
}
